<router>
{
    name: 'Flow Analytics',
}
</router>
<template lang="pug">

v-container(class="flow-analytics-main").mt-8

    v-row(justify="center" v-if="isOutOfSync")
        v-col(cols="10")
            .out-of-sync-banner
                v-icon.warning-icon warning
                span Data is out of sync. Please contact Dorsett Controls for assistance.

    v-row(align="end")
        v-col(cols="3" class="d-flex justify-start" style="padding-bottom: 12px;")
            v-tooltip(top)
                template(v-slot:activator="{ on, attrs }")
                    v-btn(icon @click="onTitleClick" v-on="on" class="brand--text")
                        v-icon(x-large) arrow_back
                span Go To Advanced Analytics
        v-col(cols="6" class="d-flex justify-center align-center" style="padding-bottom: 12px;" )
            .d-flex.align-center
                .text-h4.brand--text Flow Analytics
                v-progress-circular(v-if="isFetching" indeterminate color="blue" size="32"  class="ml-3")
        v-col(cols="3" class="d-flex justify-end")
            .d-flex.flex-column.align-items-center
                .text-h5.brand--text.text-center.mb-1 Select System
                .d-flex.align-center
                    v-icon(x-large style="height: 40px; width: 40px;" class="mr-2" color="brand" @click="getData") refresh
                    v-select(v-model="systemId", :items="systems", item-text="system_name", item-value="id", outlined, light, dense, hide-details, :menu-props="{ contentClass: 'custom-menu' }", class="always-outline", style="width: 250px;")

    v-row(justify='center' align="center" v-if="errorMessage")
        v-col(cols='10')
            v-card(class="error-message-card mb-4 overflow-hidden")
                .d-flex.align-center
                    .error-indicator
                    .d-flex.align-center.py-2.pl-3.pr-2.flex-grow-1
                        v-icon(color="error" size="20" class="mr-3") error_outline
                        .font-weight-medium.flex-grow-1 {{ errorMessage }}
                        v-btn(icon small @click="errorMessage = ''")
                            v-icon(size="18") close

    v-row(justify='center' v-if='noDataForSystem')
        v-col(cols='10')
            v-card(class="info-message-card mb-4 overflow-hidden")
                .d-flex.align-center
                    .info-indicator
                    .d-flex.align-center.py-2.pl-3.pr-3.flex-grow-1
                        v-icon(color="info" size="20" class="mr-3") info
                        .font-weight-medium No data found for this system

    // Separator line
    div(class="centered-separator-with-text" v-if="!isFetching && isInitialized")
        span.centered-separator-label Latest Prediction Snapshot of {{ snapshotDate }} {{ snapshotDayOfWeek }}

    v-row(v-if="!isFetching && isInitialized")
        v-col(cols="1")
            // (empty or spacer if needed)
        v-col(justify='left', cols='7')
            FlowChart(
                :seriesData="charts['Today'].seriesData",
                :name="charts['Today'].title",
                :productionVolume="charts['Today'].productionVolume",
                :productionTime="charts['Today'].productionTime",
                :startDate="chartDate(charts['Today'].startDate)"
                :productionVolumeUnit="charts['Today'].displayUnit",
                :rateUnit="currentSystemRateUnit",
                :yMaxRate="yaxisRateMax * futureMaxMultiplier",
                :yMaxVolume="yaxisVolumeMax * futureMaxMultiplier",
            )
        v-col(cols="4")
            OptimizationInfo(
                :optimizationParameters="optimizationParameters",
                :optimizationOriginalParameters="optimizationOriginalParameters",
                :optimizationStats="optimizationStats",
                :latestSeriesData="charts['Today']?.seriesData || []"
            )

    // Separator line
    div(class="centered-separator-with-text" v-if="!isFetching && isInitialized")
        span.centered-separator-label History

    v-row(v-if="!isFetching && isInitialized")
        v-col(cols='6')
            FlowChart(
                :seriesData="charts['2 Days Ago'].seriesData",
                :name="charts['2 Days Ago'].title"
                :productionVolume="charts['2 Days Ago'].productionVolume",
                :productionTime="charts['2 Days Ago'].productionTime",
                :startDate="chartDate(charts['2 Days Ago'].startDate)",
                :isHistorical="true"
                :productionVolumeUnit="charts['Today'].displayUnit",
                :rateUnit="currentSystemRateUnit",
                :yMaxRate="yaxisRateMax * futureMaxMultiplier",
                :yMaxVolume="yaxisVolumeMax * futureMaxMultiplier",
            )
        v-col(cols='6')
            FlowChart(
                :seriesData="charts['Yesterday'].seriesData",
                :name="charts['Yesterday'].title",
                :productionVolume="charts['Yesterday'].productionVolume",
                :productionTime="charts['Yesterday'].productionTime",
                :startDate="chartDate(charts['Yesterday'].startDate)",
                :isHistorical="true"
                :productionVolumeUnit="charts['Today'].displayUnit",
                :rateUnit="currentSystemRateUnit",
                :yMaxRate="yaxisRateMax * futureMaxMultiplier",
                :yMaxVolume="yaxisVolumeMax * futureMaxMultiplier",
            )

    // Separator line
    div(class="centered-separator-with-text" v-if="!isFetching && isInitialized")
        span.centered-separator-label Additional Predictions from Snapshot of {{ snapshotDate }}

    v-row(v-if="!isFetching && isInitialized")
        v-col(cols='6' v-if="charts.hasOwnProperty('Tomorrow')")
            FlowChart(
                :seriesData="charts['Tomorrow'].seriesData",
                :name="charts['Tomorrow'].title",
                :productionVolume="charts['Tomorrow'].productionVolume",
                :productionTime="charts['Tomorrow'].productionTime"
                :startDate="chartDate(charts['Tomorrow'].startDate)"
                :productionVolumeUnit="charts['Today'].displayUnit",
                :rateUnit="currentSystemRateUnit",
                :yMaxRate="yaxisRateMax * futureMaxMultiplier",
                :yMaxVolume="yaxisVolumeMax * futureMaxMultiplier",
            )
        v-col(cols='6' v-if="charts.hasOwnProperty('2 Days From Today')")
            FlowChart(
                :seriesData="charts['2 Days From Today'].seriesData",
                :name="charts['2 Days From Today'].title",
                :productionVolume="charts['2 Days From Today'].productionVolume",
                :productionTime="charts['2 Days From Today'].productionTime",
                :startDate="chartDate(charts['2 Days From Today'].startDate)"
                :productionVolumeUnit="charts['Today'].displayUnit",
                :rateUnit="currentSystemRateUnit",
                :yMaxRate="yaxisRateMax * futureMaxMultiplier",
                :yMaxVolume="yaxisVolumeMax * futureMaxMultiplier",
            )
    v-row(v-if="!isFetching && isInitialized")
        v-col(cols='6' v-if="charts.hasOwnProperty('3 Days From Today')")
            FlowChart(
                :seriesData="charts['3 Days From Today'].seriesData",
                :name="charts['3 Days From Today'].title",
                :productionVolume="charts['3 Days From Today'].productionVolume",
                :productionTime="charts['3 Days From Today'].productionTime",
                :startDate="chartDate(charts['3 Days From Today'].startDate)"
                :productionVolumeUnit="charts['Today'].displayUnit",
                :rateUnit="currentSystemRateUnit",
                :yMaxRate="yaxisRateMax * futureMaxMultiplier",
                :yMaxVolume="yaxisVolumeMax * futureMaxMultiplier",
            )
        v-col(cols='6' v-if="charts.hasOwnProperty('4 Days From Today')")
            FlowChart(
                :seriesData="charts['4 Days From Today'].seriesData",
                :name="charts['4 Days From Today'].title",
                :productionVolume="charts['4 Days From Today'].productionVolume",
                :productionTime="charts['4 Days From Today'].productionTime",
                :startDate="chartDate(charts['4 Days From Today'].startDate)"
                :productionVolumeUnit="charts['Today'].displayUnit",
                :rateUnit="currentSystemRateUnit",
                :yMaxRate="yaxisRateMax * futureMaxMultiplier",
                :yMaxVolume="yaxisVolumeMax * futureMaxMultiplier",
            )
</template>

<script lang="js">

import FlowChart from '@/components/portal/analytics/FlowChart';
import OptimizationInfo from '@/components/portal/analytics/OptimizationInfo';
import { DateTime } from 'luxon';
import { mapGetters } from 'vuex';

export default {
    components: {
        FlowChart, OptimizationInfo
    },
    emits: ['getFlowData'],
    data () {
        return {
            charts: {},
            selectedCustomer: null,
            customerId: null,
            isFetching: false,
            isInitialized: false,
            selectedDate: null,
            errorMessage: '',
            internalSystemId: null,
            noDataForSystem: false,
            yaxisRateMax: 0,
            yaxisVolumeMax: 0,
            historyMaxMultiplier: 1.05,
            futureMaxMultiplier: 1.1,
            isOutOfSync: false,
            optimizationParameters: {
                productionMax: null,
                storageMax: null,
                storageMin: null
            },
            optimizationOriginalParameters: {
                productionMax: null,
                storageMax: null,
                storageMin: null
            },
            optimizationStats: {
                foundFeasible: false,
                totalAttempts: 0,
                productionMinAttempt: 0,
            },
        };
    },
    computed: {
        ...mapGetters({
            currentCustomerId: 'customerId',
            systems: 'analyticsConfig',
        }),
        systemId: {
            get () {
                return this.internalSystemId !== null
                    ? this.internalSystemId
                    : this.systems.length > 0
                        ? this.systems[0].id
                        : null;
            },
            set (value) {
                this.internalSystemId = value;
                if (value) {
                    this.getData();
                }
            }
        },

        optimizerConfig () {
            return this.optimizationOriginalParameters;
        },

        snapshotParts () {
            return this.chartDateParts(this.charts?.Today?.startDate);
        },

        snapshotDate () {
            return this.snapshotParts.snapshotDate;
        },

        snapshotDayOfWeek () {
            return this.snapshotParts.snapshotDayOfWeek;
        },

        currentSystemRateUnit () {
            return this.systems.find(system => system.id === this.systemId)?.flow_production_uom || '';
        }
    },
    watch: {
        internalSystemId (newVal, oldVal) {
            if (newVal !== oldVal) {
                this.isInitialized = false;
            }
        },
    },
    methods: {
        init () {
            if(!this.currentCustomerId){
                this.$router.push('/customer/analytics');
                return;
            }
            if(this.systems.length > 0){
                this.internalSystemId = this.systems[0].id;
                this.getData();
            }
        },

        parseStartDateParts (value = '') {
            const withoutTags = value.replaceAll(/<[^>]*>/g, '').trim();
            if (!withoutTags) {
                return {
                    date: '',
                    dayOfWeek: ''
                };
            }

            const normalized = withoutTags.replaceAll(/&raquo;|»/g, '|');
            const parts = normalized
                .split('|')
                .map(part => part.trim())
                .filter(Boolean);

            return {
                date: parts[0] || '',
                dayOfWeek: parts[1] || ''
            };
        },

        async getData () {
            // fetch data from API or websocket
            if (this.isFetching) return;
            this.isOutOfSync = false;
            this.noDataForSystem = false;
            this.customerId = this.currentCustomerId;
            this.errorMessage = '';
            this.isInitialized = false;
            this.isFetching = true;

            try {
                const result = await new Promise((resolve) => {
                    this.socketEmit('getFlowData', { systemId: this.systemId }, resolve);
                });

                if (result && result.error) {
                    if (result.error.startsWith('no data')) {
                        this.noDataForSystem = true;
                        return;
                    } else if (result.error === 'not feasible') {
                        this.errorMessage = `Optimization not possible for this system. \n Check the system configuration and try again.`;
                        return;
                    } else {
                        this.errorMessage = `Error: ${result.error}`;
                        console.error('Failed to fetch data:', result.error);
                        return;
                    }
                }

                const defaultParams = { productionMax: null, storageMax: null, storageMin: null };
                const defaultStats = { foundFeasible: false, totalAttempts: 0, productionMinAttempt: 0 };


                // Compute a global y-axis max across all charts
                let globalRateMax = 0;
                let globalVolumeMax = 0;
                (result?.data || []).forEach(chart => {
                    let maxDemand = 0;
                    let maxProduction = 0;
                    let maxStorage = 0;
                    if(chart?.missing){
                        this.$set(this.charts, chart.name, chart);
                        return;
                    }
                    chart.seriesData.forEach(series => {
                        if (series.name === 'demand') {
                            maxDemand = Math.max(maxDemand, ...series.data);
                        } else if (series.name === 'production') {
                            maxProduction = Math.max(maxProduction, ...series.data);
                        } else if (series.name === 'storage') {
                            maxStorage = Math.max(maxStorage, ...series.data);
                        }
                    });
                    const localRateMax = Math.max(maxDemand, maxProduction);
                    globalRateMax = Math.max(globalRateMax, localRateMax);
                    globalVolumeMax = Math.max(globalVolumeMax, maxStorage);

                    chart.seriesData.forEach(series => {
                        if (series.name === 'storage') {
                            series.yAxis = 1;
                        } else if (series.name === 'production') {
                            series.yAxis = 0;
                            series.step = 'center';
                        } else {
                            series.yAxis = 0;
                        }
                    });

                    if (chart.title !== chart.name) {
                        if (!this.isOutOfSync) {
                            this.isOutOfSync = true;
                        }
                        chart.problem = true;
                    }

                    this.$set(this.charts, chart.name, chart);
                });
                this.yaxisRateMax = globalRateMax;
                this.yaxisVolumeMax = globalVolumeMax;

                // Safely merge to ensure reactive shape and avoid spreading undefined
                this.$set(this, 'optimizationParameters', { ...defaultParams, ...(result?.optimizationParams ) });
                this.$set(this, 'optimizationOriginalParameters', { ...defaultParams, ...(result?.optimizationOriginalParams ) });
                this.$set(this, 'optimizationStats', { ...defaultStats, ...(result?.optimizationStats ) });

                this.isInitialized = true;
            } catch (error) {
                this.errorMessage = `Error fetching data: ${error.message}`;
                console.error('Failed to fetch data:', error);
            } finally {
                this.isFetching = false;
            }
        },

        chartDate (strDate) {
            const { snapshotDate, snapshotDayOfWeek } = this.chartDateParts(strDate);
            if (!snapshotDate) {
                return '';
            }
            return `<b>${snapshotDate} &raquo; <b>${snapshotDayOfWeek}</b>`;
        },

        chartDateParts (strDate) {
            if (!strDate) {
                return {
                    snapshotDate: '',
                    snapshotDayOfWeek: ''
                };
            }

            const dt = DateTime.fromISO(strDate, { zone: 'utc' });
            if (!dt.isValid) {
                return {
                    snapshotDate: '',
                    snapshotDayOfWeek: ''
                };
            }

            return {
                snapshotDate: dt.toFormat('yyyy-MM-dd'),
                snapshotDayOfWeek: dt.toFormat('cccc')
            };
        },

        onTitleClick () {
            this.$router.push('/customer/analytics');
        }
    },
    mounted () {
        this.init();
    },

    created () {
        this.$store.dispatch('connectSocket');
    }
};
</script>

<style scoped>
    .flow-analytics-main {
        max-width: 90%;
    }

    .custom-menu .v-select-list {
        color: #443e3e !important; /* List item text color */
        background-color: rgb(0,53,92) !important; /* Dark background color of menu edges */
    }

    .always-outline >>> .v-input__slot {
        border: 1px solid #1976D2 !important; /* Replace with your desired color */
    }

    .always-outline >>> .v-input__slot:hover {
        border: 1px solid #115293 !important; /* Optional: Change on hover */
    }

    .error-message-card {
        border-radius: 4px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .error-indicator {
        width: 6px;
        min-height: 100%;
        background-color: #FF5252;
    }

    .info-message-card {
        border-radius: 4px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .out-of-sync-banner {
        display: flex;
        align-items: center;
        background: #fff3cd;
        color: #856404;
        border: 1px solid #ffeeba;
        border-radius: 6px;
        padding: 8px 16px;
        margin-bottom: 12px;
        font-weight: bold;
        font-size: 1.1em;
    }

    .warning-icon {
        color: #ff9800;
        margin-right: 8px;
        font-size: 1.3em;
    }

    .centered-separator-with-text {
        display: flex;
        align-items: center;
        width: 80%;
        margin: 32px auto 24px auto;
    }

    .centered-separator-with-text::before,
    .centered-separator-with-text::after {
        content: "";
        flex: 1;
        border-bottom: 2px solid #1976D2;
        border-radius: 2px;
    }

    .centered-separator-label {
        margin: 0 18px;
        color: #1976D2;
        font-weight: 600;
        font-size: 1.1em;
        white-space: nowrap;
    }

</style>
